import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, Output } from "@angular/core";
import { FormGroup, ReactiveFormsModule } from "@angular/forms";
import { LabelMode } from "src/utils/label-mode.enum";
import { SelectComponent } from "../select/select.component";

interface TimeOptionsCache {
  options: unknown[];
  value: unknown;
  result: unknown[];
}

@Component({
  selector: "app-time-range-select",
  templateUrl: "./time-range-select.component.html",
  styleUrl: "./time-range-select.component.scss",
  imports: [CommonModule, ReactiveFormsModule, SelectComponent],
})
export class TimeRangeSelectComponent {
  @Input({ required: true }) formGroup!: FormGroup;
  @Input({ required: true }) startControlName!: string;
  @Input({ required: true }) endControlName!: string;
  @Input() options: any[] = [];
  @Input() label = "เวลาสอน";
  @Input() required = false;
  @Input() startError = false;
  @Input() endError = false;
  @Input() startErrorMessage = "กรุณากรอกข้อมูล";
  @Input() endErrorMessage = "กรุณากรอกข้อมูล";
  @Input() inputClass = "";
  @Input() isDisabledStart?: boolean;
  @Input() isDisabledEnd?: boolean;

  @Output() startValueChange = new EventEmitter<unknown>();
  @Output() endValueChange = new EventEmitter<unknown>();

  readonly LabelMode = LabelMode;

  // `options` is a fixed grid (e.g. 30-minute steps 7:30–21:00). ng-select only
  // renders a selected value that exists in its items, so a stored off-grid time
  // (e.g. "15:55") would display blank. These getters guarantee each select's own
  // current value is present in its option list. Results are memoized so we return
  // a stable array reference across change-detection cycles (no needless re-render).
  private startOptionsCache?: TimeOptionsCache;
  private endOptionsCache?: TimeOptionsCache;

  get startDisabled(): boolean {
    return this.isDisabledStart ?? this.formGroup.get(this.startControlName)?.disabled === true;
  }

  get endDisabled(): boolean {
    return this.isDisabledEnd ?? this.formGroup.get(this.endControlName)?.disabled === true;
  }

  get startOptions(): unknown[] {
    const value: unknown = this.formGroup.get(this.startControlName)?.value;
    this.startOptionsCache = this.resolveOptions(value, this.startOptionsCache);
    return this.startOptionsCache.result;
  }

  get endOptions(): unknown[] {
    const value: unknown = this.formGroup.get(this.endControlName)?.value;
    this.endOptionsCache = this.resolveOptions(value, this.endOptionsCache);
    return this.endOptionsCache.result;
  }

  private resolveOptions(value: unknown, cache: TimeOptionsCache | undefined): TimeOptionsCache {
    const options: unknown[] = this.options;

    if (cache?.options === options && cache.value === value) {
      return cache;
    }

    const hasValue = value !== null && value !== undefined && value !== "";
    const result = hasValue && !options.includes(value) ? [value, ...options] : options;

    return { options, value, result };
  }
}
